import * as React from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, assignRole, fetchDirectory, userPhotoUrl } from "../api";
import { OrgUser } from "../types";
import { roleLabel } from "@/lib/roles";

/** Only assignable roles are offered. "Employee" is the absence of a role, so
 *  adding someone "as an Employee" would be a no-op row on the page - the
 *  picker therefore requires an actual grant (see db/roles_store.py). */
type RoleChoice = "Finance" | "Admin";
const PAGE_SIZE = 6;

/** Every assignable role, in the order the picker lists them. The dropdown
 *  always shows both and disables the ones a user already holds (granting one
 *  again is a no-op) rather than hiding them, so the full set stays visible. */
const ALL_ROLES: RoleChoice[] = ["Finance", "Admin"];

/** The assignable roles a user doesn't already hold. Used to default their
 *  pre-selected role to one that will actually grant something. A user who
 *  holds both is filtered out of the list entirely (see `filtered`), so this
 *  is always non-empty for a row that renders. */
function grantableRoles(user: OrgUser): RoleChoice[] {
  return ALL_ROLES.filter((r) => !user.roles.includes(r));
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

export default function AddUsersDialog({
  open,
  onOpenChange,
  onDone
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [directory, setDirectory] = React.useState<OrgUser[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState<Record<string, RoleChoice>>(
    {}
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Shows a read-only list of exactly who's about to be granted what role,
  // on top of this picker rather than replacing it — "Confirm & add" opens
  // this instead of calling confirm() directly; this dialog's own confirm
  // is what actually does.
  const [reviewOpen, setReviewOpen] = React.useState(false);

  // The directory is fetched live from Graph on each open rather than held by
  // the parent page, which now lists only role-holders.
  React.useEffect(() => {
    if (!open) {
      setSearch("");
      setPage(1);
      setSelected({});
      setError(null);
      setReviewOpen(false);
      return;
    }
    setLoading(true);
    fetchDirectory()
      .then(setDirectory)
      .catch(() => setDirectory([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    // Already holds both assignable roles — nothing left to grant them, so
    // they're not offered as a pickable row at all.
    const assignable = directory.filter(
      (u) => !(u.roles.includes("Admin") && u.roles.includes("Finance"))
    );
    if (!q) return assignable;
    return assignable.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [directory, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedIds = Object.keys(selected);

  const toggle = (user: OrgUser, checked: boolean) => {
    setSelected((s) => {
      if (!checked) {
        const { [user.id]: _drop, ...rest } = s;
        return rest;
      }
      // Default to the first role they can actually be granted (one they
      // don't already hold) - never one they already have, which would be a
      // no-op on confirm.
      return { ...s, [user.id]: grantableRoles(user)[0] };
    });
  };

  const confirm = async () => {
    setSaving(true);
    setError(null);
    // Sequential, not Promise.all: a batch can partially succeed (e.g. an
    // Admin grant lands, then a second Approver grant 409s) - running one at
    // a time means we know exactly which one failed and can say so, instead
    // of an all-or-nothing report that doesn't match what the server did.
    const entries = Object.entries(selected);
    for (const [userId, role] of entries) {
      // Re-confirming someone who already holds this exact role is a no-op
      // by design - skip the call entirely instead of hitting the
      // backend's now-enforced already-assigned check and having it
      // surface as a batch-aborting error for what isn't actually one.
      const alreadyHasRole = directory
        .find((u) => u.id === userId)
        ?.roles.includes(role);
      if (alreadyHasRole) continue;
      try {
        await assignRole(userId, role);
      } catch (err) {
        const who =
          directory.find((u) => u.id === userId)?.displayName || userId;
        const message =
          err instanceof ApiError ? err.message : `Could not update ${who}.`;
        setError(message);
        setSaving(false);
        onDone(); // refresh the page's list with whatever did succeed so far
        return;
      }
    }
    setSaving(false);
    setReviewOpen(false);
    onDone();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("sm:max-w-2xl", PHONE_SHEET_DIALOG)}>
        <DialogHeader>
          <DialogTitle>Add users</DialogTitle>
          <DialogDescription>
            Pick people from the Microsoft Entra directory and assign their
            role. Only users with a role appear on the User Management page.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search the directory by name or email…"
            className="pl-9"
          />
        </div>

        <Separator />

        <div className="min-h-[20rem] min-w-0 space-y-1">
          {loading &&
            Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} className="flex min-w-0 items-center gap-3 px-1 py-2">
                <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-40 max-w-full" />
                  <Skeleton className="h-3 w-56 max-w-full" />
                </div>
                <Skeleton className="h-8 w-[7.5rem] shrink-0" />
              </div>
            ))}

          {!loading &&
            pageRows.map((u) => {
              const isSelected = u.id in selected;
              const sub = [u.designation, u.department]
                .filter(Boolean)
                .join(" / ");
              const grantable = grantableRoles(u);
              const rowDefault = grantable[0];
              return (
                // Wraps onto a second line on narrow screens (role picker
                // drops below the identity block) rather than overflowing -
                // a dialog this row lives in is already width-constrained.
                <div
                  key={u.id}
                  className="flex min-w-0 flex-wrap items-center gap-3 rounded-md px-1 py-2 hover:bg-muted/60"
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(v) => toggle(u, !!v)}
                    aria-label={`Select ${u.displayName}`}
                  />
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={userPhotoUrl(u.id)} alt="" />
                    <AvatarFallback className="text-xs">
                      {initials(u.displayName || u.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 basis-40">
                    <p className="truncate text-sm font-medium">
                      {u.displayName}
                      {sub && (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          ({sub})
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {u.email}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2 pl-9 sm:pl-0">
                    {u.roles.length > 0 && (
                      <Badge variant="secondary" className="font-normal">
                        {u.roles.map(roleLabel).join(", ")}
                      </Badge>
                    )}
                    <Select
                      value={selected[u.id] ?? rowDefault}
                      onValueChange={(v) =>
                        setSelected((s) => ({ ...s, [u.id]: v as RoleChoice }))
                      }
                      disabled={!isSelected}
                    >
                      <SelectTrigger className="h-8 w-[8.125rem]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_ROLES.map((r) => {
                          const held = u.roles.includes(r);
                          return (
                            <SelectItem key={r} value={r} disabled={held}>
                              {r === "Admin" ? "Admin" : roleLabel(r)}
                              {held && (
                                <span className="text-muted-foreground"></span>
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}

          {!loading && pageRows.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {directory.length === 0
                ? "Could not load the Entra directory."
                : "No users match this search."}
            </p>
          )}
        </div>

        <Separator />

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {filtered.length === 0
              ? "0"
              : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)}`}{" "}
            of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <span>
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <DialogFooter className={cn("sm:justify-between", PHONE_DIALOG_FOOTER)}>
          <span className="self-center text-xs text-muted-foreground">
            {selectedIds.length === 0
              ? "Select users to add"
              : `${selectedIds.length} selected`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => setReviewOpen(true)}
              disabled={selectedIds.length === 0}
            >
              Continue
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className={cn("sm:max-w-md", PHONE_SHEET_DIALOG)}>
          <DialogHeader>
            <DialogTitle>Confirm role assignment</DialogTitle>
            <DialogDescription>
              {selectedIds.length === 1
                ? "This person will be granted the role shown below."
                : `These ${selectedIds.length} people will be granted the roles shown below.`}
            </DialogDescription>
          </DialogHeader>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <ScrollArea className="-mx-6 max-h-[20rem]">
            <div className="space-y-1 px-6">
              {selectedIds.map((userId) => {
                const user = directory.find((u) => u.id === userId);
                const role = selected[userId];
                return (
                  <div
                    key={userId}
                    className="flex items-center justify-between gap-3 py-1.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {user?.displayName || userId}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      {roleLabel(role)}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewOpen(false)}
              disabled={saving}
            >
              Back
            </Button>
            <Button onClick={confirm} disabled={saving}>
              {saving ? "Adding…" : "Confirm & add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
